import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { commonToast } from '../../../helper/Toast';
// import { BiImport } from 'react-icons/bi';
import { FaPlus, FaEdit, FaTrash } from 'react-icons/fa';
import { IoMdSearch } from 'react-icons/io';
import Select from 'react-select';
import TailwindDataTable from '../../../components/TailwindDataTable';
import PageSizeSelectBox from '../../../components/PageSizeSelectBox';
import ReactPaginate from 'react-paginate';
import { getSelectedSchoolId } from '../../../helper/commonFunctions';
import AddParentModel from '../../../components/model/AddParentModel';
import { parseListResponse, searchParents } from '../../../helper/adminUsersApi';


export default function ParentsList() {
    const { t } = useTranslation();
    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [searchQuery, setSearchQuery] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');
    const [parents, setParents] = useState([]);
    const [limit, setLimit] = useState(10);
    const [loading, setLoading] = useState(false);
    const [isAddOpen, setIsAddOpen] = useState(false);
    const schoolId = getSelectedSchoolId();

    const columns = [
        {
            key: 'name',
            label: t('name'),
            formatter: (_, row) => {
                if (!row) return null;
                const fullName = `${row.firstName || ''} ${row.lastName || ''}`.trim() || row.name || '';
                const initials = fullName ? fullName.split(' ').map(n => n[0]).join('') : '?';
                return (
                    <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                            <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                                <span className="text-gray-500 text-sm font-medium">
                                    {initials}
                                </span>
                            </div>
                        </div>
                        <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">{fullName}</div>
                            <div className="text-sm text-gray-500">{row.email || ''}</div>
                        </div>
                    </div>
                );
            }
        },
        {
            key: 'phone',
            label: t('phone'),
            formatter: (row) => row || ''
        },
        {
            key: 'children',
            label: t('mychild'),
            formatter: (cell, row) => {
                const kids = cell || row.schoolChildList || [];
                if (!Array.isArray(kids) || kids.length === 0) return '-';
                return (
                    <div className="flex flex-wrap gap-1">
                        {kids.map((child, index) => (
                            <span key={index} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                {typeof child === 'string' ? child : child.studentId || child.name || '-'}
                            </span>
                        ))}
                    </div>
                );
            }
        },
        {
            key: 'status',
            label: t('status'),
            formatter: (cell) => {
                console.log('cell :>> ', cell);
                if (!cell) return null;
                return (
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${String(cell).toUpperCase() === 'ACTIVE' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                        {t(`parent_management.${String(cell).toLowerCase()}`, { defaultValue: cell })}
                    </span>
                );
            }
        },
        {
            key: 'lastLogin',
            label: t('parent_management.last_login'),
            formatter: (cell) => cell || ''
        },
        {
            key: 'actions',
            label: t('actions'),
            formatter: (_, cell) => {
                if (!cell) return null;
                return (
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => handleEditParent(cell)}
                            className="text-blue-600 hover:text-blue-800 transition-colors duration-200"
                            title={t('edit')}
                        >
                            <FaEdit size={18} />
                        </button>
                        <button
                            onClick={() => handleDeleteParent(cell)}
                            className="text-red-600 hover:text-red-800 transition-colors duration-200"
                            title={t('remove')}
                        >
                            <FaTrash size={18} />
                        </button>
                    </div>
                );
            }
        }
    ];

    const fetchParents = async () => {
        try {
            setLoading(true);
            const response = await searchParents({
                offset: currentPage * limit,
                limit,
                schoolId,
            });
            setParents(parseListResponse(response));
        } catch (error) {
            console.error('Error fetching parents:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchParents();
    }, [currentPage, limit]);

    const handleEditParent = (parent) => {
        if (!parent) return;
        commonToast(t('parent_management.edit_success'), 'success');
    };

    const handleDeleteParent = (parent) => {
        if (!parent) return;
        commonToast(t('parent_management.delete_success'), 'success');
    };

    const handlePageChange = (selectedItem) => {
        setCurrentPage(selectedItem.selected);
    };

    const handleSearch = (e) => {
        setSearchQuery(e.target.value);
    };

    const handleStatusFilter = (selectedOption) => {
        setFilterStatus(selectedOption.value);
    };

    const filteredParents = parents.filter(parent => {
        const fullName = `${parent.firstName || ''} ${parent.lastName || ''} ${parent.name || ''}`.toLowerCase();
        const matchesSearch = fullName.includes(searchQuery.toLowerCase()) ||
            (parent.email || '').toLowerCase().includes(searchQuery.toLowerCase());
        const matchesStatus = filterStatus === 'all' || String(parent.status || '').toLowerCase() === filterStatus;
        return matchesSearch && matchesStatus;
    });

    console.log('Filtered parents:', filteredParents);
    console.log('Current page:', currentPage);
    console.log('Page size:', pageSize);

    const statusOptions = [
        { value: 'all', label: t('all') },
        { value: 'active', label: t('parent_management.active') },
        { value: 'inactive', label: t('parent_management.inactive') }
    ];

    return (
        <div className="container mx-auto px-4 py-8">
            <div className="bg-white rounded-lg shadow p-6">
                {/* Header */}
                <div className="flex flex-wrap justify-between items-center mb-6">
                    <h1 className="text-2xl font-bold text-gray-900">{t('parent_management.title')}</h1>
                    <div className="flex gap-4 md:mt-4 sm:flex-col">
                        {/* <button className="primary-btn">
                            <BiImport size={20} />
                            {t('parent_management.import_csv')}
                        </button> */}
                        <button
                            type="button"
                            onClick={() => setIsAddOpen(true)}
                            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                        >
                            <FaPlus size={20} />
                            {t('parent_management.add_parent')}
                        </button>
                    </div>
                </div>

                {/* Search and Filters */}
                <div className="flex flex-wrap gap-4 mb-6">
                    <div className="flex-1 min-w-[200px]">
                        <div className="relative">
                            <input
                                type="text"
                                placeholder={t('parent_management.search_placeholder')}
                                className="w-1/2 md:w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                value={searchQuery}
                                onChange={handleSearch}
                            />
                            <IoMdSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                        </div>
                    </div>
                    <div className="min-w-[200px]">
                        <Select
                            options={statusOptions}
                            placeholder={t('parent_management.filter_by_status')}
                            onChange={handleStatusFilter}
                            defaultValue={statusOptions[0]}
                            className="react-select-container"
                            classNamePrefix="react-select"
                        />
                    </div>
                </div>

                {/* Data Table */}
                <>
                    <TailwindDataTable
                        columns={columns}
                        data={filteredParents}
                        thClasses='text-white'
                        loading={loading}
                    />

                    {/* Pagination Controls */}
                    <div className="flex justify-between items-center mt-4">
                        <div className='xs:mb-5'>
                            <PageSizeSelectBox selected={limit} setSelected={setLimit} setPage={setCurrentPage} />
                        </div>
                        <div className='flex gap-4'>
                            <ReactPaginate
                                breakLabel="..."
                                nextLabel=">"
                                previousLabel="<"
                                onPageChange={({ selected }) => { setCurrentPage(selected + 1) }}
                                pageRangeDisplayed={2}
                                forcePage={currentPage - 1}
                                pageCount={Math.ceil(filteredParents.length / limit)}
                                containerClassName="flex gap-2"
                                pageClassName="px-3 py-1 border rounded hover:bg-gray-100"
                                activeClassName="bg-blue-500 text-white"
                                previousClassName="px-3 py-1 border rounded hover:bg-gray-100"
                                nextClassName="px-3 py-1 border rounded hover:bg-gray-100"
                                disabledClassName="opacity-50 cursor-not-allowed"
                                renderOnZeroPageCount={null}
                            />
                        </div>
                    </div>
                </>
                <AddParentModel
                    open={isAddOpen}
                    setOpen={setIsAddOpen}
                    onCreated={fetchParents}
                />
            </div>
        </div>
    );
}
